import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router";

import {
  resolveSwipeUnlockScrollIntent,
  shouldApplySwipeUnlockScrollIntent,
  type SwipeUnlockScrollIntent,
} from "./swipeUnlockScrollHide";

type SwipeUnlockChromeContextValue = {
  enforcementActive: boolean;
  setEnforcementActive: (active: boolean) => void;
  /** True when the bar is translated off-screen due to scroll-down. */
  scrollHidden: boolean;
  /** Extra bottom inset reserves space for the slider while enforcement is active. */
  insetActive: boolean;
};

const SwipeUnlockChromeContext = createContext<SwipeUnlockChromeContextValue | null>(null);

export function SwipeUnlockChromeProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [enforcementActive, setEnforcementActiveState] = useState(false);
  const [scrollHidden, setScrollHidden] = useState(false);
  const scrollHiddenRef = useRef(false);
  const lastScrollYRef = useRef(0);
  const lastIntentRef = useRef<SwipeUnlockScrollIntent | null>(null);
  const lastToggleAtRef = useRef(0);

  const setEnforcementActive = useCallback((active: boolean) => {
    setEnforcementActiveState(active);
    if (!active) {
      scrollHiddenRef.current = false;
      lastIntentRef.current = null;
      setScrollHidden(false);
    }
  }, []);

  useEffect(() => {
    lastScrollYRef.current = window.scrollY;
    scrollHiddenRef.current = false;
    lastIntentRef.current = null;
    setScrollHidden(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!enforcementActive) {
      return;
    }

    const applyIntent = (intent: SwipeUnlockScrollIntent) => {
      const now = Date.now();
      if (
        !shouldApplySwipeUnlockScrollIntent(
          scrollHiddenRef.current,
          intent,
          lastIntentRef.current,
          lastToggleAtRef.current,
          now,
        )
      ) {
        return;
      }

      lastIntentRef.current = intent;
      lastToggleAtRef.current = now;
      const nextHidden = intent === "hide";
      scrollHiddenRef.current = nextHidden;
      setScrollHidden(nextHidden);
    };

    const onScroll = () => {
      const currentY = window.scrollY;
      const intent = resolveSwipeUnlockScrollIntent(
        lastScrollYRef.current,
        currentY,
        scrollHiddenRef.current,
      );
      lastScrollYRef.current = currentY;

      if (intent === null) {
        return;
      }

      applyIntent(intent);
    };

    lastScrollYRef.current = window.scrollY;
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [enforcementActive]);

  const insetActive = enforcementActive;

  useEffect(() => {
    if (insetActive) {
      document.documentElement.dataset.screen0SwipeUnlockInset = "true";
    } else {
      delete document.documentElement.dataset.screen0SwipeUnlockInset;
    }

    return () => {
      delete document.documentElement.dataset.screen0SwipeUnlockInset;
    };
  }, [insetActive]);

  const value = useMemo(
    () => ({
      enforcementActive,
      setEnforcementActive,
      scrollHidden,
      insetActive,
    }),
    [enforcementActive, insetActive, scrollHidden, setEnforcementActive],
  );

  return (
    <SwipeUnlockChromeContext.Provider value={value}>{children}</SwipeUnlockChromeContext.Provider>
  );
}

export function useSwipeUnlockChrome(): SwipeUnlockChromeContextValue {
  const context = useContext(SwipeUnlockChromeContext);
  if (!context) {
    throw new Error("useSwipeUnlockChrome must be used within SwipeUnlockChromeProvider");
  }
  return context;
}
